import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import {
  ShieldCheck, History, AlertCircle, Loader2, RefreshCw, Info,
  Stethoscope, Calendar, Activity, Cog,
} from "lucide-react";

/**
 * MOT Awareness Panel — MOT-1 UI surface
 * --------------------------------------------------------------------------
 * Single shared panel consumed in two operational roles:
 *
 *   mode="foh"     — Continuity layer on the Intake session screen. Shows
 *                    what the FOH needs operationally (repeated advisories,
 *                    previous declines, customer concern summary).
 *   mode="tester"  — Pre-test informational layer on the technician VI
 *                    screen. Shows what the tester should know before
 *                    starting (repeated advisories with persistence trend,
 *                    previous advisories on the most recent test,
 *                    customer concerns).
 *
 * Architectural constraints (mirrored from MOT_INTELLIGENCE_ENGINE):
 *   • DVSA data is READ-ONLY. The panel never offers actions that mutate it.
 *   • NO "likely fail" wording. NO sales / upsell tone.
 *   • NO auto-classification of failure. Tester independently inspects.
 *   • Tester wording (when shown) is preserved verbatim.
 *
 * Visual tone:
 *   • Calm slate/teal palette. No badges flashing in red unless DVSA
 *     itself flagged a long-standing item.
 *   • Sections are quiet — only render when they carry content.
 *   • Italic disclaimer line in every panel making the guardrails explicit.
 *
 * Reference: /app/memory/INTAKE_ORCHESTRATION_ARCHITECTURE.md (MOT Booking)
 */
export default function MOTAwarenessPanel({
  sessionId,
  jobId,
  mode = "foh",
  className = "",
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const endpoint = sessionId
    ? `${API}/intake/sessions/${sessionId}/mot-intelligence`
    : jobId
      ? `${API}/jobs/${jobId}/mot-intelligence`
      : null;

  const load = async () => {
    if (!endpoint) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(endpoint);
      setData(r.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load MOT awareness");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  if (!endpoint) return null;

  if (loading) {
    return (
      <section
        className={`rounded-lg border border-slate-200 bg-white px-5 py-4 ${className}`}
        data-testid="mot-awareness-panel-loading"
      >
        <p className="text-[14px] uppercase tracking-[0.18em] text-slate-700 font-bold flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading MOT awareness…
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section
        className={`rounded-lg border border-slate-200 bg-white px-5 py-4 ${className}`}
        data-testid="mot-awareness-panel-error"
      >
        <p className="text-[15px] text-slate-700 italic">
          MOT awareness unavailable for this vehicle.
        </p>
      </section>
    );
  }

  const isTester = mode === "tester";
  const tester = data?.tester_panel || {};
  const foh = data?.foh_panel || {};
  const history = data?.history_summary || {};
  const concerns = data?.customer_concerns || [];

  // Empty-state — nothing to show. Stay quiet but render a soft placeholder
  // so the FOH/Tester sees the panel exists and is "all clear".
  const repeated = (isTester ? tester.repeated_advisories : foh.repeated_advisories) || [];
  const previousAdvisories = tester.previous_advisories || [];
  const declines = foh.previous_declines || [];
  const concernSummary = foh.concern_summary || [];
  const counts = foh.counts || {};
  const dangerousHistory = tester.dangerous_history_count || 0;
  const mostRecent = history.most_recent_test || null;

  const hasAnything =
    repeated.length > 0
    || (isTester && previousAdvisories.length > 0)
    || (!isTester && declines.length > 0)
    || (!isTester && concernSummary.length > 0)
    || (isTester && concerns.length > 0)
    || dangerousHistory > 0
    || (mostRecent && mostRecent.advisory_count > 0)
    || (isTester && _hasFohCapture(data?.foh_mot_capture));

  // Stage 4 — Diagnostic Operational Alerts. Restrained, deterministic.
  // Fires only when the customer's wording matches a curated rule set
  // (ABS / engine light / DPF / AdBlue / overheating / wont-start / etc.).
  // Empty array = calm default.
  const operationalAlerts = Array.isArray(data?.operational_alerts)
    ? data.operational_alerts
    : [];
  const alertKindLabels = data?.operational_alert_kind_labels || {};
  const hasAlerts = operationalAlerts.length > 0;

  // Stage 6 — Timing-belt awareness. Backend returns `null` when below
  // the eligibility threshold; the card only renders for high-confidence
  // signals (wet-belt engine, interval exceeded, age+mileage strongly
  // suggest due, or no replacement record on file for an old high-miler).
  const timingBelt = data?.timing_belt_awareness || null;

  return (
    <section
      className={`rounded-lg border border-slate-200 bg-white ${className}`}
      data-testid={`mot-awareness-panel-${mode}`}
      data-mode={mode}
    >
      <header className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldCheck className="w-5 h-5 text-teal-600 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-[14px] uppercase tracking-[0.18em] text-slate-700 font-bold">
              {isTester ? "MOT awareness — tester" : "MOT awareness — FOH"}
            </p>
            <p className="text-[14px] text-slate-500 leading-snug mt-0.5">
              {isTester
                ? "Pre-test continuity. Inspect the vehicle independently."
                : "Continuity reference for the conversation."}
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="text-slate-400 hover:text-slate-700 p-1.5 -m-1.5 rounded transition-colors"
          aria-label="Refresh"
          data-testid="mot-awareness-refresh-btn"
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </header>

      <div className="px-5 py-4 space-y-5">
        {/* Disclaimer — explicit, calm. */}
        <p
          className="text-[14px] italic text-slate-600 leading-relaxed flex items-start gap-2"
          data-testid="mot-awareness-disclaimer"
        >
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
          <span>
            {isTester
              ? "Informational only. Severity is your call after inspection — this panel never predicts a fail."
              : "Continuity only. No automatic pricing. The customer decides what they want to action."}
          </span>
        </p>

        {/* Stage 4 — Diagnostic Operational Alerts. */}
        {hasAlerts && (
          <OperationalAlertsBlock
            alerts={operationalAlerts}
            kindLabels={alertKindLabels}
          />
        )}

        {/* Phase 2 — FOH capture surface. Shows the FOH's Stage-3 MOT
            companion capture verbatim (advisories the FOH discussed,
            customer-awareness flag, pre-test concerns) so the tester
            sees what was on the call without parsing foh_notes. */}
        {isTester && data?.foh_mot_capture && (
          <FohMotCaptureBlock capture={data.foh_mot_capture} />
        )}

        {/* Stage 6 — Timing-belt awareness. Surfaces ONLY when the
            evidence threshold is met (wet-belt engine / interval exceeded /
            age+mileage strongly suggest due / no record on file). Calm,
            preventative, advisory. */}
        {timingBelt && (
          <TimingBeltAwarenessBlock data={timingBelt} />
        )}

        {!hasAnything && !hasAlerts && !timingBelt && (
          <div className="text-[15px] text-slate-700 italic" data-testid="mot-awareness-empty">
            Nothing of operational note — no repeated advisories, no previous declines, no flagged concerns.
          </div>
        )}

        {/* Repeated advisories — top item for both modes */}
        {repeated.length > 0 && (
          <RepeatedAdvisoriesBlock items={repeated} mode={mode} />
        )}

        {/* Tester — previous advisories on most recent test */}
        {isTester && previousAdvisories.length > 0 && (
          <PreviousAdvisoriesBlock items={previousAdvisories} />
        )}

        {/* Customer concerns — FOH aggregated, Tester raw with low-confidence flag */}
        {isTester && concerns.length > 0 && (
          <ConcernsTesterBlock concerns={concerns} />
        )}
        {!isTester && concernSummary.length > 0 && (
          <ConcernsFOHBlock summary={concernSummary} />
        )}

        {/* FOH — previous declines */}
        {!isTester && declines.length > 0 && (
          <DeclinesBlock items={declines} />
        )}

        {/* Dangerous history — Tester only. Calm, not alarming. */}
        {isTester && dangerousHistory > 0 && (
          <div
            className="rounded-md border border-slate-200 bg-slate-50 px-3.5 py-2.5"
            data-testid="mot-awareness-dangerous-history"
          >
            <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold">
              Historical context
            </p>
            <p className="text-[15px] text-slate-700 leading-snug mt-1">
              This vehicle has had {dangerousHistory}{" "}
              {dangerousHistory === 1 ? "DANGEROUS item" : "DANGEROUS items"}{" "}
              recorded on previous MOTs. Independent inspection still applies — context only.
            </p>
          </div>
        )}

        {/* FOH — counts row */}
        {!isTester && hasAnything && (
          <CountsRow counts={counts} />
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

function OperationalAlertsBlock({ alerts, kindLabels }) {
  return (
    <div data-testid="mot-awareness-operational-alerts">
      <p className="text-[14px] uppercase tracking-[0.16em] text-amber-700 font-bold mb-2 flex items-center gap-1.5">
        <Activity className="w-3.5 h-3.5 text-amber-600" />
        Operational alerts
      </p>
      <ul className="space-y-2">
        {alerts.map((alert) => (
          <li
            key={alert.id}
            className="rounded-md border border-amber-200 bg-amber-50/60 px-3.5 py-2.5"
            data-testid={`mot-awareness-alert-${alert.id}`}
          >
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {(alert.kinds || []).map((k) => (
                <AlertKindChip key={k} kind={k} label={kindLabels[k] || k} />
              ))}
            </div>
            {/* What the FOH should say — italic, like a senior advisor whispering */}
            <p className="text-[15px] text-slate-800 leading-snug italic">
              "{alert.foh_say}"
            </p>
            {/* Workshop note */}
            {alert.workshop_note && (
              <p
                className="text-[14px] text-slate-700 mt-1.5 flex items-start gap-1.5 leading-snug"
                data-testid={`alert-${alert.id}-workshop-note`}
              >
                <Stethoscope className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-500" />
                <span>{alert.workshop_note}</span>
              </p>
            )}
            {/* Scheduling hint — only when set */}
            {alert.schedule_hint && (
              <p
                className="text-[14px] text-slate-700 mt-1 flex items-start gap-1.5 leading-snug"
                data-testid={`alert-${alert.id}-schedule-hint`}
              >
                <Calendar className="w-3 h-3 mt-0.5 flex-shrink-0 text-slate-500" />
                <span>{alert.schedule_hint}</span>
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AlertKindChip({ kind, label }) {
  const tones = {
    mot_impacting:     "bg-red-50 text-red-800 border-red-200",
    diagnostic_needed: "bg-blue-50 text-blue-800 border-blue-200",
    scheduling_risk:   "bg-amber-100 text-amber-900 border-amber-300",
  };
  const tone = tones[kind] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span
      className={`text-[13px] uppercase tracking-wide font-bold border rounded-full px-2.5 py-1 ${tone}`}
      data-testid={`alert-kind-${kind}`}
    >
      {label}
    </span>
  );
}

function TimingBeltAwarenessBlock({ data }) {
  const tones = {
    wet_belt:            "bg-violet-50 text-violet-900 border-violet-200",
    interval_exceeded:   "bg-amber-50 text-amber-900 border-amber-300",
    interval_likely_due: "bg-amber-50 text-amber-900 border-amber-200",
    history_unknown:     "bg-slate-100 text-slate-700 border-slate-200",
  };
  const tone = tones[data.confidence] || tones.history_unknown;
  return (
    <div data-testid="mot-awareness-timing-belt">
      <p className="text-[14px] uppercase tracking-[0.16em] text-violet-700 font-bold mb-2 flex items-center gap-1.5">
        <Cog className="w-3.5 h-3.5 text-violet-600" />
        Timing-belt awareness
      </p>
      <div
        className="rounded-md border border-violet-200 bg-violet-50/40 px-3.5 py-2.5"
        data-testid={`timing-belt-confidence-${data.confidence}`}
      >
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          <span
            className={`text-[13px] uppercase tracking-wide font-bold border rounded-full px-2.5 py-1 ${tone}`}
            data-testid="timing-belt-confidence-chip"
          >
            {data.confidence_label}
          </span>
          {data.engine_label && (
            <span
              className="text-[13px] uppercase tracking-wide font-bold border border-slate-200 bg-white text-slate-700 rounded-full px-2.5 py-1"
              data-testid="timing-belt-engine-chip"
            >
              {data.engine_label}
            </span>
          )}
        </div>
        {/* What the FOH should say — italic, senior-advisor tone */}
        <p
          className="text-[15px] text-slate-800 leading-snug italic"
          data-testid="timing-belt-foh-say"
        >
          "{data.foh_say}"
        </p>
        {/* Evidence */}
        {Array.isArray(data.evidence) && data.evidence.length > 0 && (
          <ul
          className="text-[14px] text-slate-700 mt-2 space-y-1 leading-snug"
            data-testid="timing-belt-evidence"
          >
            {data.evidence.map((line, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="text-violet-500 mt-0.5">·</span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        )}
        {/* Calm guardrail line — explicit advisory framing. */}
        <p className="text-[13.5px] italic text-violet-700 mt-2 leading-snug">
          Preventative-maintenance advisory only. Not part of the MOT — purely a reminder if no replacement record is on file.
        </p>
      </div>
    </div>
  );
}

function RepeatedAdvisoriesBlock({ items, mode }) {
  return (
    <div data-testid="mot-awareness-repeated">
      <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2">
        Repeated advisories
      </p>
      <ul className="space-y-2.5">
        {items.map((item, i) => (
          <li
            key={i}
            className="rounded-md border border-slate-200 bg-white px-3.5 py-2.5"
            data-testid={`mot-awareness-repeated-item-${i}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-[15px] text-slate-800 leading-snug font-medium">
                  {mode === "tester" && item.example ? item.example : capitalise(item.advisory_text)}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  <PersistenceChip trend={item.persistence_trend} occurrences={item.occurrence_count} />
                  {item.category && item.category !== "other" && (
                    <CategoryChip category={item.category} />
                  )}
                  {item.last_seen_date && (
                    <span className="text-[13px] text-slate-500">
                      last seen {formatDate(item.last_seen_date)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PreviousAdvisoriesBlock({ items }) {
  return (
    <div data-testid="mot-awareness-previous">
      <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5 text-slate-400" />
        Previous advisories (most recent test)
      </p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li
            key={i}
            className="text-[15px] text-slate-700 leading-snug"
            data-testid={`mot-awareness-previous-item-${i}`}
          >
            <span className="text-slate-400 mr-1.5">·</span>
            {item.example || capitalise(item.advisory_text)}
            {item.last_seen_date && (
              <span className="text-slate-400 ml-1.5">({formatDate(item.last_seen_date)})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConcernsTesterBlock({ concerns }) {
  return (
    <div data-testid="mot-awareness-concerns-tester">
      <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2">
        Customer concerns at booking
      </p>
      <ul className="space-y-2">
        {concerns.map((c, i) => (
          <li
            key={i}
            className="rounded-md border border-slate-200 bg-white px-3.5 py-2.5"
            data-testid={`mot-awareness-concern-${i}`}
          >
            <p className="text-[13.5px] text-slate-800 italic leading-snug">
              &ldquo;{c.free_text}&rdquo;
            </p>
            {Array.isArray(c.labels) && c.labels.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {c.labels.map((lbl, k) => (
                  <span
                    key={k}
                    className="text-[11px] uppercase tracking-wide bg-slate-100 text-slate-600 rounded-full px-2 py-0.5 font-semibold"
                  >
                    {lbl}
                  </span>
                ))}
              </div>
            )}
            {c.low_confidence && (
              <p className="text-[11.5px] text-amber-700 mt-1.5 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                Tag not auto-detected — FOH may have classified manually.
              </p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ConcernsFOHBlock({ summary }) {
  return (
    <div data-testid="mot-awareness-concerns-foh">
      <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2">
        Customer concerns
      </p>
      <ul className="space-y-2">
        {summary.map((tag, i) => (
          <li
            key={i}
            className="rounded-md border border-slate-200 bg-white px-3.5 py-2.5"
            data-testid={`mot-awareness-concern-tag-${i}`}
          >
            <p className="text-[13.5px] text-slate-800 font-medium leading-snug">
              {tag.label}
            </p>
            {Array.isArray(tag.examples) && tag.examples.length > 0 && (
              <ul className="mt-1 space-y-0.5">
                {tag.examples.slice(0, 2).map((ex, k) => (
                  <li key={k} className="text-[12.5px] text-slate-500 italic leading-snug">
                    &ldquo;{ex}&rdquo;
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DeclinesBlock({ items }) {
  return (
    <div data-testid="mot-awareness-declines">
      <p className="text-[14px] uppercase tracking-[0.16em] text-slate-700 font-bold mb-2">
        Previously declined
      </p>
      <ul className="space-y-1.5">
        {items.map((item, i) => (
          <li
            key={i}
            className="text-[15px] text-slate-700 leading-snug"
            data-testid={`mot-awareness-decline-${i}`}
          >
            <span className="text-slate-400 mr-1.5">·</span>
            {item.summary}
            {item.declined_date && (
              <span className="text-slate-400 ml-1.5">({formatDate(item.declined_date)})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function CountsRow({ counts }) {
  const cells = [
    { label: "Repeated", value: counts.repeated_count, key: "repeated_count" },
    { label: "Open concerns", value: counts.open_concerns, key: "open_concerns" },
    { label: "Prev. declines", value: counts.previous_declines, key: "previous_declines" },
  ].filter((c) => typeof c.value === "number");
  if (cells.length === 0) return null;
  return (
    <div
      className="flex flex-wrap gap-3 pt-2 border-t border-slate-100"
      data-testid="mot-awareness-counts"
    >
      {cells.map((c) => (
        <div
          key={c.key}
          className="flex-1 min-w-[80px] text-center"
          data-testid={`mot-awareness-count-${c.key}`}
        >
          <p className="text-[20px] font-bold text-slate-800 leading-none">{c.value}</p>
          <p className="text-[11px] uppercase tracking-wide text-slate-500 mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function PersistenceChip({ trend, occurrences }) {
  const tones = {
    longstanding: "bg-amber-50 text-amber-800 border-amber-200",
    repeated:     "bg-slate-100 text-slate-700 border-slate-200",
    new:          "bg-white text-slate-500 border-slate-200",
    resolved:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  const tone = tones[trend] || tones.new;
  const label = (() => {
    switch (trend) {
      case "longstanding": return `Long-standing · ${occurrences || 0}× MOTs`;
      case "repeated":     return `Repeated · ${occurrences || 0}× MOTs`;
      case "resolved":     return "Previously raised — not on most recent test";
      default:             return "First time advisory";
    }
  })();
  return (
    <span
      className={`text-[11px] uppercase tracking-wide font-semibold border rounded-full px-2 py-0.5 ${tone}`}
      data-testid={`persistence-chip-${trend || "new"}`}
    >
      {label}
    </span>
  );
}

function CategoryChip({ category }) {
  return (
    <span
      className="text-[11px] uppercase tracking-wide bg-teal-50 text-teal-700 border border-teal-100 rounded-full px-2 py-0.5 font-semibold"
      data-testid={`category-chip-${category}`}
    >
      {category}
    </span>
  );
}

function capitalise(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}


// ---------------------------------------------------------------------------
// Phase 2 — FOH MOT capture (read-only surface for the tester).
//
// Renders the Stage-3 MOT companion capture verbatim. Each sub-block is
// only shown when the FOH actually entered something — no empty rails.
// ---------------------------------------------------------------------------

function _hasFohCapture(capture) {
  if (!capture || typeof capture !== "object") return false;
  return Boolean(
    (capture.last_advisories || "").trim()
      || (capture.pre_test_concerns || "").trim()
      || capture.customer_aware_of_advisories
      || capture.mot_due_date
      || capture.preferred_slot_window
  );
}

function FohMotCaptureBlock({ capture }) {
  if (!_hasFohCapture(capture)) return null;

  const advisories = (capture.last_advisories || "").trim();
  const preTestConcerns = (capture.pre_test_concerns || "").trim();
  const aware = capture.customer_aware_of_advisories;
  const slot = capture.preferred_slot_window || "";

  const awareLabel = {
    yes: "Customer aware",
    no: "Customer NOT aware",
    unsure: "Customer unsure",
  }[aware];
  const awareTone = {
    yes: "bg-emerald-50 text-emerald-800 border-emerald-200",
    no: "bg-rose-50 text-rose-800 border-rose-200",
    unsure: "bg-amber-50 text-amber-800 border-amber-200",
  }[aware] || "bg-slate-50 text-slate-700 border-slate-200";

  return (
    <div
      className="rounded-lg border border-teal-200 bg-teal-50/40 px-4 py-3"
      data-testid="foh-mot-capture-block"
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-teal-800 font-bold">
          From the FOH call
        </p>
        {slot && (
          <span className="text-[11.5px] text-slate-600">
            Slot: <span className="font-semibold">{slot}</span>
          </span>
        )}
      </div>

      {advisories && (
        <div className="mb-2.5" data-testid="foh-capture-advisories">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-teal-700 font-semibold mb-1">
            Last advisories (as captured)
          </p>
          <pre className="text-[14px] text-slate-900 leading-snug whitespace-pre-wrap font-sans bg-white border border-slate-200 rounded-md px-3 py-2">
            {advisories}
          </pre>
        </div>
      )}

      {awareLabel && (
        <div className="mb-2.5">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[12.5px] font-semibold ${awareTone}`}
            data-testid="foh-capture-aware"
          >
            {awareLabel}
          </span>
        </div>
      )}

      {preTestConcerns && (
        <div data-testid="foh-capture-pre-test-concerns">
          <p className="text-[10.5px] uppercase tracking-[0.18em] text-teal-700 font-semibold mb-1">
            Pre-test concerns
          </p>
          <p className="text-[14px] text-slate-900 italic leading-snug bg-white border border-slate-200 rounded-md px-3 py-2">
            “{preTestConcerns}”
          </p>
        </div>
      )}
    </div>
  );
}

